import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { classNames } from "primereact/utils";
import styles from "./DurationField.module.scss";
import { AppDialog } from "../../../AppDialog/AppDialog";
import { AppTooltip } from "../../../tooltip/AppTooltip";
import { DefaultButton } from "../../../button/defaultButton/DefaultButton";
import { Icon, IconType } from "../../../../icons/Icon";
import { useAppContext } from "../../../../../AppContext";
import {
  DurationMode,
  dialogToMinutes,
  formatDuration,
  initDurationDialog,
} from "../../../../infrastructure/utilities/durationUtility";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IDurationFieldProps {
  name: string;
  label?: string;
  tooltip?: string;
  required?: boolean;
  disabled?: boolean;
  width?: string;
}

interface IDurationDialogContentProps {
  initialMinutes: number | null | undefined;
  onSave: (totalMinutes: number) => void;
}

const clampInt = (raw: string, min: number, max: number): number => {
  const parsed = parseInt(raw, 10);
  if (Number.isNaN(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
};

const DurationDialogContent = ({
  initialMinutes,
  onSave,
}: IDurationDialogContentProps) => {
  const init = initDurationDialog(initialMinutes);
  const [mode, setMode] = useState<DurationMode>(init.mode);
  const [hours, setHours] = useState(init.hours);
  const [minutes, setMinutes] = useState(init.minutes);
  const [days, setDays] = useState(init.days);

  const handleSave = () =>
    onSave(dialogToMinutes({ mode, hours, minutes, days }));

  return (
    <div className={styles.dialog}>
      <div
        className={classNames(styles.card, {
          [styles.card_active]: mode === "time",
        })}
        onClick={() => setMode("time")}
      >
        <span className={styles.card_radio} />
        <span className={styles.card_title}>Часове и минути</span>
        <div className={styles.clock}>
          <div className={styles.clock_segment}>
            <input
              className={styles.clock_input}
              type="number"
              min={0}
              max={23}
              value={hours}
              onFocus={() => setMode("time")}
              onChange={(e) => setHours(clampInt(e.target.value, 0, 23))}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
            />
            <span className={styles.clock_unit}>часа</span>
          </div>

          <span className={styles.clock_colon}>:</span>

          <div className={styles.clock_segment}>
            <input
              className={styles.clock_input}
              type="number"
              min={0}
              max={59}
              value={minutes}
              onFocus={() => setMode("time")}
              onChange={(e) => setMinutes(clampInt(e.target.value, 0, 59))}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
            />
            <span className={styles.clock_unit}>мин</span>
          </div>
        </div>
      </div>

      <div className={styles.separator}>
        <span>или</span>
      </div>

      <div
        className={classNames(styles.card, {
          [styles.card_active]: mode === "days",
        })}
        onClick={() => setMode("days")}
      >
        <span className={styles.card_radio} />
        <span className={styles.card_title}>Дни</span>
        <div className={styles.days}>
          <input
            className={styles.days_input}
            type="number"
            min={0}
            max={365}
            value={days}
            onFocus={() => setMode("days")}
            onChange={(e) => setDays(clampInt(e.target.value, 0, 365))}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
          />
          <span className={styles.days_unit}>{days === 1 ? "ден" : "дни"}</span>
        </div>
      </div>

      <DefaultButton label="Запази" onClick={handleSave} width="100%" />
    </div>
  );
};

export const DurationField = ({
  name,
  label,
  tooltip,
  required = false,
  disabled = false,
  width,
}: IDurationFieldProps) => {
  const { control } = useFormContext();
  const { isDesktopOrTablet } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);

  if (!width) {
    width = isDesktopOrTablet ? "600px" : dvSize("80dvw");
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div style={{ width }} className={styles.wrapper}>
          {label && (
            <div
              className={classNames(styles.label, {
                [styles.label_error]: !!fieldState.error?.message,
              })}
            >
              {label}
              {required ? "*" : ""}
              <AppTooltip content={tooltip} />
            </div>
          )}

          <div
            className={classNames(styles.trigger, {
              [styles.trigger_disabled]: disabled,
            })}
            onClick={() => !disabled && setIsOpen(true)}
          >
            <span className={styles.trigger_value}>
              {field.value
                ? formatDuration(field.value)
                : "Изберете продължителност"}
            </span>
            <Icon type={IconType.ClockIcon} color="#fff" />
          </div>

          {fieldState.error?.message && (
            <div className={styles.error}>{fieldState.error.message}</div>
          )}

          <AppDialog
            visible={isOpen}
            onHide={() => setIsOpen(false)}
            header={label ?? "Продължителност"}
            headerIconType={IconType.ClockIcon}
            style={{ width: "90%", maxWidth: "420px" }}
          >
            {isOpen && (
              <DurationDialogContent
                initialMinutes={field.value}
                onSave={(totalMinutes) => {
                  field.onChange(totalMinutes);
                  setIsOpen(false);
                }}
              />
            )}
          </AppDialog>
        </div>
      )}
    />
  );
};
